import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { client } from '../../../../apollo'
import { UPGRADE_TO_BASIC_SETUP } from '../../../../graphql/enterprisePlan'

type FormState = {
  enterprisePlanId: number | null
  domains: string[]
}

export default function UpgradeToBasicSetupForm() {
  const [formData, setFormData] = useState<FormState>({
    enterprisePlanId: null,
    domains: [''],
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [submittedData, setSubmittedData] = useState<FormState | null>(null)
  const [enterprisePlanId, setEnterprisePlanId] = useState<number | null>(null)

  const [upgradeToBasicSetup, { loading }] = useMutation(
    UPGRADE_TO_BASIC_SETUP,
    {
      onCompleted: (data) => {
        const result = data.adminUpgradeToBasicSetupEnterprisePlan
        if (result.enterprisePlanId) {
          setSuccess(true)
          setEnterprisePlanId(result.enterprisePlanId)
          setSubmittedData({ ...formData })
        }
      },
      onError: (err) => {
        setError(
          err.message || 'Failed to upgrade to Basic Setup enterprise plan',
        )
      },
      client,
    },
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    let parsedValue: string | number = value
    if (name === 'enterprisePlanId' && value) {
      parsedValue = parseInt(value, 10)
    }

    setFormData({
      ...formData,
      [name]: parsedValue,
    })
  }

  const handleDomainChange = (index: number, value: string) => {
    const updatedDomains = [...formData.domains]
    updatedDomains[index] = value
    setFormData({
      ...formData,
      domains: updatedDomains,
    })
  }

  const addDomainField = () => {
    setFormData({
      ...formData,
      domains: [...formData.domains, ''],
    })
  }

  const removeDomainField = (index: number) => {
    const updatedDomains = [...formData.domains]
    updatedDomains.splice(index, 1)
    setFormData({
      ...formData,
      domains: updatedDomains.length > 0 ? updatedDomains : [''],
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setSubmittedData(null)

    if (!formData.enterprisePlanId) {
      setError('Please select an enterprise plan to upgrade')
      return
    }

    // Filter out empty domains
    const filteredDomains = formData.domains.filter(
      (domain) => domain.trim() !== '',
    )

    if (filteredDomains.length === 0) {
      setError('At least one domain is required')
      return
    }

    try {
      await upgradeToBasicSetup({
        variables: {
          enterprisePlanId: formData.enterprisePlanId,
          domains: filteredDomains,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div>
      {success && submittedData && (
        <div>
          <h3>Enterprise plan successfully upgraded to Basic Setup!</h3>
          <p>Enterprise Plan ID: {enterprisePlanId}</p>

          <h4>Basic Setup Configuration:</h4>
          <p>
            Employees with email addresses at these domains can now sign up for
            and use Goby with the enterprise plan features.
          </p>
        </div>
      )}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="enterprisePlanId">
            Bulk License Plan to Upgrade
            <input
              type="number"
              id="enterprisePlanId"
              name="enterprisePlanId"
              value={formData.enterprisePlanId || ''}
              onChange={handleInputChange}
              placeholder="Enter Enterprise Plan ID"
              required
            />
          </label>
        </div>

        <div>
          <div>
            <span id="domains-label">Domains</span>
            {formData.domains.map((domain, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`domain-${index}`}>
                <label htmlFor={`domain-${index}`}>
                  <span>Domain {index + 1}</span>
                  <input
                    type="text"
                    id={`domain-${index}`}
                    value={domain}
                    onChange={(e) => handleDomainChange(index, e.target.value)}
                    placeholder="example.com"
                    aria-labelledby="domains-label"
                    required={index === 0}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeDomainField(index)}
                  disabled={formData.domains.length === 1 && index === 0}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addDomainField}>
              Add Domain
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Upgrading...' : 'Upgrade to Basic Setup Plan'}
        </button>
      </form>
    </div>
  )
}
